<template>
	<div>
		<div v-if="currentStep <= schema.steps.length" class="flex flex-col gap-8">
			<header>
				<img
					v-if="coverImage"
					:src="coverImage"
					loading="lazy"
					class="flex-none w-[100px] rounded float-left mr-4 mb-1"
					@error="coverImage = false"
				/>
				<n-text
					type="primary"
					class="text-lg min-[480px]:text-xl font-bold -mt-0.5 mb-1 leading-tight"
				>
					{{ selectedMode === 'info' ? infoModeTitle : copy.title }}
				</n-text>
				<p v-html="copy.description" />
			</header>

			<div v-if="schema.steps.length > 1" class="mode-selector">
				<p class="mode-selector__label">Cosa vuoi fare?</p>

				<div class="mode-selector__grid">
					<button
						type="button"
						class="mode-card"
						:class="{'mode-card--active': selectedMode === 'availability'}"
						@click="selectAvailabilityMode"
					>
						<div class="mode-card__head">
							<div class="mode-card__title-wrap">
								<n-icon class="mode-card__icon"><i-mdi-bed-empty /></n-icon>
								<span class="mode-card__title">{{ availabilityModeCardTitle }}</span>
							</div>

							<n-icon v-if="selectedMode === 'availability'" class="mode-card__check">
								<i-carbon-checkmark />
							</n-icon>
						</div>

						<p class="mode-card__text">
							{{ availabilityModeCardText }}
						</p>
					</button>

					<button
						type="button"
						class="mode-card"
						:class="{'mode-card--active': selectedMode === 'info'}"
						@click="selectInfoMode"
					>
						<div class="mode-card__head">
							<div class="mode-card__title-wrap">
								<n-icon class="mode-card__icon"><i-carbon-user-avatar-filled /></n-icon>
								<span class="mode-card__title">{{ infoModeCardTitle }}</span>
							</div>

							<n-icon v-if="selectedMode === 'info'" class="mode-card__check">
								<i-carbon-checkmark />
							</n-icon>
						</div>

						<p class="mode-card__text">{{ infoModeCardText }}</p>
					</button>
				</div>
			</div>

			<div v-if="schema.steps.length > 1 && selectedMode === 'availability'" class="steps-wrap">
				<n-steps :current="currentStep" class="form-steps form-steps--readonly">
					<n-step
						v-for="(step, index) in schema.steps"
						:key="step.key"
						:title="getStepTitle(index + 1)"
					>
						<template #icon>
							<n-icon>
								<i-mdi-bed-empty v-if="step.icon === 'mdi-bed-empty'" />
								<i-carbon-user-avatar-filled v-if="step.icon === 'carbon-user-avatar-filled'" />
								<i-mdi-silverware-fork-knife v-if="step.icon === 'mdi-silverware-fork-knife'" />
								<i-mdi-hiking v-if="step.icon === 'mdi-hiking'" />
								<i-mdi-ski v-if="step.icon === 'mdi-ski'" />
							</n-icon>
						</template>
					</n-step>
				</n-steps>
			</div>

			<n-form :model="formModel" size="medium" class="form-grid">
				<template
					v-for="field in schema.fields
						.filter(field => !field.key.includes('.'))
						.filter(
							field =>
								(!field.show_if || field.show_if.every(x => formModel[x])) &&
								schema.steps.findIndex(x => x.key === field.step) + 1 === currentStep &&
								!(
									selectedMode === 'info' &&
									schema.steps.findIndex(x => x.key === field.step) + 1 === firstStepNumber
								)
						)"
					:key="field.key"
				>
					<!-- container start -->
					<div v-if="field.element === 'container'" class="col-span-6 flex flex-col gap-2">
						<div
							v-for="(item, index) in formModel[field.key]"
							:key="index"
							class="w-full flex flex-col gap-4"
							:class="{
								'bg-slate-200/75 p-4 rounded': formModel[field.key].length > 1,
							}"
						>
							<header
								v-if="formModel[field.key].length > 1"
								class="flex justify-between items-center"
							>
								<p class="font-medium pl-0.5">{{ field.label }} {{ index + 1 }}</p>
								<n-button
									type="error"
									size="tiny"
									secondary
									icon-placement="right"
									@click="formModel[field.key].splice(index, 1)"
								>
									{{ t$.remove }}
									<template #icon>
										<n-icon><i-carbon-trash-can /></n-icon>
									</template>
								</n-button>
							</header>
							<div
								class="form-grid"
								:class="{
									'pointer-events-none opacity-60 select-none':
										field.key === 'rooms' && !isRoomValidIndex(index),
								}"
							>
								<template
									v-for="child in schema.fields
										.filter(x => x.key.startsWith(`${field.key}.`))
										.filter(
											x =>
												!x.show_if ||
												x.show_if.every(y => {
													const [parentKey, childKey] = y.split('.')
													return formModel[parentKey][index][childKey]
												})
										)"
									:key="child.key"
								>
									<fields
										v-model:field-value="v$[field.key].$model[index][child.key.split('.').pop()]"
										:data="child"
										:language="language"
										:errors="
											v$[field.key].$each.$response.$errors[index][child.key.split('.').pop()]
										"
									/>
								</template>
							</div>

							<div
								v-if="field.key === 'rooms' && !isRoomValidIndex(index)"
								class="mt-2 text-xs text-amber-700 italic"
							>
								Non valida per questa struttura (max {{ minisiteData.max_rooms_request }} camera/e).
								Non verrà inviata.
							</div>
						</div>
						<div v-if="field.multiple" class="w-full">
							<!-- warning SEMPRE visibile se ci sono camere extra -->
							<div
								v-if="field.key === 'rooms' && invalidRoomsCount > 0"
								class="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
							>
								Questa struttura accetta al massimo
								{{ Number.isFinite(maxRooms) ? maxRooms : '—' }} camera/e:
								{{ invalidRoomsCount }} camera/e non verranno incluse nella richiesta.
							</div>

							<!-- ✅ UN SOLO PULSANTE: se disabled, lo wrappiamo in tooltip -->
							<n-tooltip
								v-if="field.key === 'rooms' && !canAddRoom"
								trigger="hover"
								placement="top"
							>
								<template #trigger>
									<span class="inline-block">
										<n-button type="info" secondary size="small" class="mt-2" disabled>
											<span class="text-xs uppercase">
												{{ t$.add }} {{ field.label.toLowerCase() }}
											</span>
											<template #icon>
												<n-icon><i-carbon-add /></n-icon>
											</template>
										</n-button>
									</span>
								</template>

								{{ addRoomDisabledReason }}
							</n-tooltip>

							<n-button
								v-else
								type="info"
								secondary
								size="small"
								class="mt-2"
								@click="
									formModel[field.key].push({
										...formModel[field.key][formModel[field.key].length - 1],
									})
								"
							>
								<span class="text-xs uppercase">{{ t$.add }} {{ field.label.toLowerCase() }}</span>
								<template #icon>
									<n-icon><i-carbon-add /></n-icon>
								</template>
							</n-button>
						</div>
					</div>
					<!-- container end -->

					<fields
						v-else
						v-model:field-value="v$[field.key].$model"
						:data="field"
						:language="language"
						:errors="v$[field.key].$errors"
					/>
				</template>

				<div v-if="verifyCaptchaAndSubmit" class="col-span-6">
					<turnstile sitekey="0x4AAAAAAABmJjGbuww8KS5I" @verify="submitRequest" />
				</div>
			</n-form>

			<div class="flex flex-col gap-4">
				<div class="flex justify-between gap-6">
					<div>
						<n-button
							v-if="selectedMode === 'availability' && currentStep > 1"
							type="default"
							tertiary
							icon-placement="left"
							@click="handlePrevStep"
						>
							{{ t$.prev_step }}
							<template #icon>
								<n-icon><i-carbon-arrow-left /></n-icon>
							</template>
						</n-button>
					</div>
					<div>
						<n-button
							v-if="
								currentStep < schema.steps.length &&
								!(selectedMode === 'info' && currentStep === firstStepNumber)
							"
							type="info"
							icon-placement="right"
							:disabled="!canProceedToNextStep"
							@click="handleNextStep"
						>
							{{ nextStepButtonLabel }}
							<template #icon>
								<n-icon><i-carbon-arrow-right /></n-icon>
							</template>
						</n-button>
						<n-button
							v-if="
								(selectedMode === 'availability' && currentStep === schema.steps.length) ||
								(selectedMode === 'info' && currentStep === infoStepNumber)
							"
							type="success"
							icon-placement="right"
							:disabled="!formValidIgnoringExtraRooms || apiLoading || verifyCaptchaAndSubmit"
							:loading="apiLoading || verifyCaptchaAndSubmit"
							@click="verifyCaptchaAndSubmit = true"
						>
							<span class="pr-2">{{ t$.send_request }}</span>
							<template #icon>
								<n-icon size="24"><i-mdi-email-fast /></n-icon>
							</template>
						</n-button>
					</div>
				</div>
				<div>
					<p v-if="stepHasRequiredFields" class="text-xs text-gray-400 italic">
						{{ t$.required_fields_notice }}
					</p>
					<p v-if="currentStep === schema.steps.length" class="text-xs text-gray-300 italic">
						{{ t$.captcha_1 }}
						<a
							href="https://www.cloudflare.com/privacypolicy/"
							target="_blank"
							rel="nofollow"
							class="hover:text-gray-400"
						>
							{{ t$.captcha_2 }}
						</a>
						{{ t$.captcha_3 }}
						<a
							href="https://www.cloudflare.com/website-terms/"
							target="_blank"
							rel="nofollow"
							class="hover:text-gray-400"
						>
							{{ t$.captcha_4 }}
						</a>
						{{ t$.captcha_5 }}
					</p>
				</div>
			</div>
		</div>

		<n-result
			v-else
			:status="apiResponse.success ? 'success' : 'error'"
			:title="apiResponse.success ? copy.result_title_success : copy.result_title_error"
		>
			<p v-if="apiResponse.success" class="text-center" v-html="copy.result_message_success" />

			<div v-else>
				<p class="text-center mb-3" v-html="resultMessageError" />
				<p class="text-center text-xs opacity-50">{{ apiResponse.message }}</p>
			</div>

			<div class="flex justify-center mt-8">
				<n-button type="info" tertiary @click="handleNewRequest">
					{{ t$.send_new_request }}
				</n-button>
			</div>
		</n-result>
	</div>
</template>

<script setup>
	import {NIcon, NSteps, NStep, NForm, NButton, NResult, NText, NTooltip} from 'naive-ui'
	import Fields from './Fields.vue'
	import {useVuelidate} from '@vuelidate/core'
	import {generateModel, generateValidationRules} from './schema'
	import {getData} from './data'
	import {getTranslations} from './translations'
	import {saveUserData} from './storage'
	import {clearFormModelFields, capitalizeFirstLetter} from './utils'
	import Turnstile from 'cfturnstile-vue3'
	import {provide, ref, computed, toRefs} from 'vue'
	import {watchDebounced} from '@vueuse/core'
	import ICarbonUserAvatarFilled from '~icons/carbon/user-avatar-filled'
	import ICarbonTrashCan from '~icons/carbon/trash-can'
	import ICarbonAdd from '~icons/carbon/add'
	import ICarbonArrowLeft from '~icons/carbon/arrow-left'
	import ICarbonArrowRight from '~icons/carbon/arrow-right'
	import IMdiEmailFast from '~icons/mdi/email-fast'
	import IMdiBedEmpty from '~icons/mdi/bed-empty'
	import IMdiSilverwareForkKnife from '~icons/mdi/silverware-fork-knife'
	import IMdiHiking from '~icons/mdi/hiking'
	import IMdiSki from '~icons/mdi/ski'
	import ICarbonCheckmark from '~icons/carbon/checkmark'

	const props = defineProps({
		formElement: Object,
		lang: String,
		minisiteCategoryId: Number,
		minisiteId: Number,
		imagesPath: String,
		coverImage: String,
	})
	const {formElement, lang, minisiteCategoryId, minisiteId, imagesPath, coverImage} = toRefs(props)

	const {minisite: minisiteData, form: formData} = await getData(
		minisiteCategoryId.value,
		minisiteId.value,
		lang.value
	)
	minisiteData.imagesPath = imagesPath.value
	const schema = formData.schema
	const language = minisiteData.language
	const t$ = getTranslations(language.id)

	provide('minisiteData', minisiteData)
	provide('t$', t$)

	const currentStep = ref(1)
	const {model, defaultValues} = generateModel(schema)
	const formModel = ref(model)
	const rules = generateValidationRules(schema, t$)
	const v$ = useVuelidate(rules, formModel.value)
	const verifyCaptchaAndSubmit = ref(false)
	const infoOnlyRequest = ref(false)
	const selectedMode = ref('availability') // 'availability' | 'info'

	const firstStepNumber = 1
	const firstStepKey = schema.steps?.[0]?.key ?? null
	const infoStepNumber = 2

	const firstStepSchemaFields = computed(() =>
		schema.fields.filter(field => field.step === firstStepKey && !field.key.includes('.'))
	)

	const firstStepFieldKeys = computed(() => firstStepSchemaFields.value.map(field => field.key))
	const firstStepFieldElements = computed(() =>
		firstStepSchemaFields.value.map(field => field.element)
	)

	const isSkiSchool = computed(() => {
		return (
			firstStepFieldKeys.value.includes('skilessontype') ||
			firstStepFieldKeys.value.includes('skilessontechnique') ||
			firstStepFieldKeys.value.includes('skilessongrade')
		)
	})

	const isGuide = computed(() => {
		return (
			firstStepFieldKeys.value.includes('event') &&
			firstStepFieldElements.value.includes('select-minisiteevent')
		)
	})

	const infoModeTitle = computed(() => {
		if (language.id === 2) return 'Send an information request'
		if (language.id === 3) return 'Informationsanfrage senden'

		if (isSkiSchool.value) return 'Invia una richiesta per una lezione di sci o per informazioni'
		if (isGuide.value) return 'Invia una richiesta'
		return 'Invia una richiesta di informazioni'
	})

	const availabilityModeCardTitle = computed(() => {
		if (language.id === 2) {
			if (isSkiSchool.value) return 'Request a lesson'
			if (isGuide.value) return 'Request an excursion or snowshoe hike'
			return 'Request availability'
		}

		if (language.id === 3) {
			if (isSkiSchool.value) return 'Unterricht anfragen'
			if (isGuide.value) return 'Ausflug oder Schneeschuhwanderung anfragen'
			return 'Verfügbarkeit anfragen'
		}

		if (isSkiSchool.value) return 'Richiedi una lezione'
		if (isGuide.value) return 'Richiedi escursione o ciaspolata'
		return 'Richiedi disponibilità'
	})

	const availabilityModeCardText = computed(() => {
		if (language.id === 2) {
			if (isSkiSchool.value) {
				return 'Enter the date and some information to check lesson availability.'
			}
			if (isGuide.value) {
				return 'Select the excursion and enter the number of participants to check availability.'
			}
			return 'Check availability by entering the date and any preferred time.'
		}

		if (language.id === 3) {
			if (isSkiSchool.value) {
				return 'Geben Sie das Datum und einige Informationen an, um die Verfügbarkeit für den Unterricht zu prüfen.'
			}
			if (isGuide.value) {
				return 'Wählen Sie den Ausflug und geben Sie die Teilnehmerzahl an, um die Verfügbarkeit zu prüfen.'
			}
			return 'Prüfen Sie die Verfügbarkeit, indem Sie Datum und eventuelle Uhrzeit angeben.'
		}

		if (isSkiSchool.value) {
			return 'Indica la data e alcune informazioni per verificare la disponibilità di una lezione.'
		}
		if (isGuide.value) {
			return "Seleziona l'escursione e indica il numero di partecipanti per verificare la disponibilità."
		}
		return 'Verifica disponibilità indicando data ed eventuale orario.'
	})

	const infoModeCardTitle = computed(() => {
		if (language.id === 2) return 'Request information'
		if (language.id === 3) return 'Informationen anfragen'
		return 'Richiedi informazioni'
	})

	const infoModeCardText = computed(() => {
		if (language.id === 2) {
			if (isSkiSchool.value)
				return 'Write to the ski school to receive information on courses, lessons and activities.'
			if (isGuide.value) return 'Ask a question about excursions or organised activities.'
			return 'Ask the operator a question without entering a date or time.'
		}

		if (language.id === 3) {
			if (isSkiSchool.value)
				return 'Schreiben Sie an die Skischule, um Informationen zu Kursen, Unterricht und Aktivitäten zu erhalten.'
			if (isGuide.value)
				return 'Stellen Sie eine Frage zu Ausflügen oder organisierten Aktivitäten.'
			return 'Stellen Sie dem Betreiber eine Frage, ohne Datum oder Uhrzeit anzugeben.'
		}

		if (isSkiSchool.value)
			return 'Scrivi alla scuola sci per ricevere informazioni su corsi, lezioni e attività.'
		if (isGuide.value) return 'Fai una domanda sulle escursioni o sulle attività organizzate.'
		return 'Fai una domanda al gestore senza indicare data o ora.'
	})

	function getStepTitle(stepNumber) {
		if (stepNumber === firstStepNumber) {
			if (language.id === 2) {
				if (isGuide.value) return 'Excursion'
				return 'Desired date'
			}

			if (language.id === 3) {
				if (isGuide.value) return 'Ausflug'
				return 'Gewünschtes Datum'
			}

			if (isGuide.value) return 'Escursione'
			return 'Data desiderata'
		}

		if (stepNumber === infoStepNumber) {
			if (language.id === 2) return 'Request'
			if (language.id === 3) return 'Anfrage'
			return 'Richiesta'
		}

		return schema.steps?.[stepNumber - 1]?.title ?? ''
	}

	const firstStepFields = computed(() =>
		schema.fields
			.filter(field => !field.key.includes('.'))
			.filter(field => field.step === firstStepKey)
			.filter(field => !field.show_if || field.show_if.every(x => formModel.value[x]))
	)

	const firstStepRequiredFields = computed(() =>
		firstStepFields.value.filter(field => field.rules?.required)
	)

	const isFirstStepValid = computed(() => {
		return firstStepFields.value.every(field => {
			if (field.key === 'rooms') {
				return roomsValid.value
			}

			return !v$.value[field.key]?.$invalid
		})
	})

	const isFirstStepIncomplete = computed(() => {
		return firstStepRequiredFields.value.some(field => {
			if (field.key === 'rooms') {
				return !roomsValid.value
			}

			return !!v$.value[field.key]?.$invalid
		})
	})

	const nextStepButtonLabel = computed(() => t$.next_step)
	const canAddRoom = computed(() => {
		const max = minisiteData.max_rooms_request

		// Se non c'è limite => sempre possibile aggiungere
		if (!max) return true

		const roomsCount = Array.isArray(formModel.value.rooms) ? formModel.value.rooms.length : 0

		return roomsCount < max
	})

	const addRoomDisabledReason = computed(() => {
		const max = minisiteData?.max_rooms_request
		if (!max) return '' // se non c'è limite, non serve tooltip
		return `E' stato raggiunto il numero massimo di camere per questa struttura`
	})

	const maxRooms = computed(() => {
		const max = minisiteData?.max_rooms_request
		// se 0/null => nessun limite
		return max ? Number(max) : Infinity
	})

	const validRoomsCount = computed(() => {
		const roomsCount = Array.isArray(formModel.value.rooms) ? formModel.value.rooms.length : 0
		return Math.min(roomsCount, maxRooms.value)
	})

	const invalidRoomsCount = computed(() => {
		const roomsCount = Array.isArray(formModel.value.rooms) ? formModel.value.rooms.length : 0
		return Math.max(0, roomsCount - (Number.isFinite(maxRooms.value) ? maxRooms.value : roomsCount))
	})

	function isRoomValidIndex(index) {
		return index < maxRooms.value
	}

	/**
	 * ✅ Vuelidate: considera valide solo le prime N camere.
	 * Torna true se le prime N camere non hanno errori.
	 */
	const roomsValid = computed(() => {
		// se rooms non esiste, non blocchiamo qui
		if (!v$.value?.rooms?.$each?.$response?.$errors) return true

		const errorsByIndex = v$.value.rooms.$each.$response.$errors
		const count = validRoomsCount.value

		for (let i = 0; i < count; i++) {
			const errObj = errorsByIndex?.[i]
			if (!errObj) continue

			// errObj è tipo: { adults: [], children: [], ... } (array di errori per campo)
			for (const arr of Object.values(errObj)) {
				if (Array.isArray(arr) && arr.length > 0) {
					return false
				}
			}
		}

		return true
	})

	/**
	 * ✅ Validazione "globale" ignorando camere in eccesso
	 * - per rooms usiamo roomsValid
	 * - per gli altri campi usiamo v$ normale
	 */
	const formValidIgnoringExtraRooms = computed(() => {
		const firstStepFieldKeys = new Set(firstStepFields.value.map(field => field.key))

		for (const key of Object.keys(v$.value)) {
			if (key.startsWith('$')) continue

			if (infoOnlyRequest.value && firstStepFieldKeys.has(key)) {
				continue
			}

			if (key === 'rooms') {
				if (!roomsValid.value) return false
				continue
			}

			if (v$.value[key]?.$invalid) return false
		}

		return true
	})

	function handleNextStep() {
		if (currentStep.value === firstStepNumber) {
			currentStep.value = infoStepNumber
			formElement.value?.scrollIntoView()
			return
		}

		currentStep.value++
		formElement.value?.scrollIntoView()
	}

	function selectAvailabilityMode() {
		selectedMode.value = 'availability'
		infoOnlyRequest.value = false
		currentStep.value = firstStepNumber
		formElement.value?.scrollIntoView()
	}

	function selectInfoMode() {
		selectedMode.value = 'info'
		infoOnlyRequest.value = true
		currentStep.value = infoStepNumber
		formElement.value?.scrollIntoView()
	}

	function handlePrevStep() {
		currentStep.value--

		if (currentStep.value <= firstStepNumber) {
			currentStep.value = firstStepNumber
		}

		formElement.value?.scrollIntoView()
	}

	const copy = computed(() => {
		const result = {}

		for (const [key, value] of Object.entries(schema.copy)) {
			result[key] = capitalizeFirstLetter(
				value
					.replace(/%%MINISITE_NAME%%/g, minisiteData.name)
					.replace('%%MINISITE_ARTICLE%%', minisiteData.article ?? '')
					.replace("' ", "'")
					.replace('%%USER_EMAIL%%', formModel.value.email ?? '—')
					.replace(/\s\s+/g, ' ')
			)
		}

		return result
	})

	const resultMessageError = computed(() => {
		const assistanceEmail = 'assistenzaform@webcloud.it'
		const error = apiResponse.value && !apiResponse.value.success ? apiResponse.value.message : ''

		return t$.result_message_error.replace(
			'%%ASSISTANCE_EMAIL%%',
			`<a href="mailto:${assistanceEmail}?subject=Assistenza%20Asiago.it%20Form&body=${encodeURIComponent(
				error
			)}">${assistanceEmail}</a>`
		)
	})

	// validate fields once if their value is different than the default value
	for (const key of Object.keys(v$.value)) {
		if (key.startsWith('$')) {
			continue
		}

		if (JSON.stringify(v$.value[key].$model) === JSON.stringify(defaultValues[key])) {
			continue
		}

		v$.value[key].$validate()
	}

	const stepHasRequiredFields = computed(() => {
		return schema.fields
			.filter(field => schema.steps.findIndex(x => x.key === field.step) + 1 === currentStep.value)
			.filter(field => !field.key.includes('.'))
			.filter(field => !field.show_if || field.show_if.every(x => formModel.value[x]))
			.some(field => field.rules?.required)
	})

	const canProceedToNextStep = computed(() => {
		if (currentStep.value === firstStepNumber) {
			return true
		}

		const fieldsInStep = schema.fields
			.filter(field => schema.steps.findIndex(x => x.key === field.step) + 1 === currentStep.value)
			.filter(field => !field.key.includes('.'))
			.filter(field => !field.show_if || field.show_if.every(x => formModel.value[x]))

		return fieldsInStep.every(field => {
			if (field.key === 'rooms') {
				return roomsValid.value
			}

			return !v$.value[field.key]?.$invalid
		})
	})

	watchDebounced(
		formModel,
		data => {
			// clear fields that are not visibile (don't meet show_if rules)
			clearFormModelFields(schema, formModel.value)

			// save user data
			saveUserData(data, schema.fields)
		},
		{deep: true, debounce: 500, maxWait: 2000}
	)

	const apiResponse = ref(null)
	const apiLoading = ref(false)

	function buildFieldsToSend() {
		const payload = {
			...formModel.value,
			info_only_request: infoOnlyRequest.value,
		}

		// normalizzazione rooms
		payload.rooms = Array.isArray(formModel.value.rooms)
			? formModel.value.rooms.slice(
					0,
					Number.isFinite(maxRooms.value) ? maxRooms.value : formModel.value.rooms.length
			  )
			: []

		// se è richiesta info-only, togli tutti i campi del primo step
		if (infoOnlyRequest.value && firstStepKey) {
			const firstStepFields = schema.fields.filter(
				field => field.step === firstStepKey && !field.key.includes('.')
			)

			for (const field of firstStepFields) {
				if (field.element === 'container') {
					payload[field.key] = []
				} else {
					delete payload[field.key]
				}
			}
		}

		return payload
	}
	async function submitRequest(captchaToken) {
		apiLoading.value = true

		const data = new FormData()
		data.append(
			'data',
			JSON.stringify({
				language,
				minisite_id: minisiteId.value,
				minisite_category_id: minisiteData.category.id,
				captcha: captchaToken,
			})
		)
		const fieldsToSend = buildFieldsToSend()
		data.append('fields', JSON.stringify(fieldsToSend))

		const req = await fetch(
			`${
				import.meta.env.DEV ? 'http://localhost:5712' : 'https://forms-api.webcloud.cloud'
			}/submit`,
			{
				method: 'POST',
				body: data,
			}
		)
		const res = await req.json()

		apiResponse.value = res
		apiLoading.value = false
		verifyCaptchaAndSubmit.value = false
		currentStep.value++
		formElement.value?.scrollIntoView()
	}

	function handleNewRequest() {
		window.location.reload()
	}
</script>

<style lang="scss" scoped>
	.form-grid {
		display: grid;
		grid-template-columns: repeat(6, 1fr);
		gap: 16px;
		width: 100%;
	}

	.mode-selector {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 14px;
	}

	.mode-selector__label {
		margin: 0;
		font-size: 13px;
		font-weight: 600;
		text-align: center;
		color: rgb(31, 34, 37);
	}

	.mode-selector__grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
		width: 100%;
		max-width: 640px;
	}

	.mode-card {
		border: 1px solid rgb(224, 224, 230);
		border-radius: 8px;
		background: #fff;
		padding: 14px 16px;
		text-align: left;
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		appearance: none;
		width: 100%;
		cursor: pointer;
	}

	.mode-card:hover {
		border-color: #1b77bf;
	}

	.mode-card--active {
		border-color: #1b77bf;
		background: rgba(27, 119, 191, 0.05);
		box-shadow: 0 0 0 1px rgba(27, 119, 191, 0.08);
	}

	.mode-card__head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		margin-bottom: 8px;
	}

	.mode-card__title-wrap {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.mode-card__icon {
		color: #1b77bf;
		font-size: 18px;
	}

	.mode-card__title {
		font-size: 14px;
		font-weight: 600;
		color: rgb(31, 34, 37);
	}

	.mode-card__check {
		color: #1b77bf;
		font-size: 18px;
	}

	.mode-card__text {
		margin: 0;
		font-size: 12px;
		line-height: 1.35;
		color: rgb(90, 96, 102);
	}

	.steps-wrap {
		display: flex;
		justify-content: center;
		width: 100%;
		margin-top: 4px;
	}

	.form-steps {
		width: 100%;
		max-width: 640px;
	}

	.form-steps--readonly {
		pointer-events: none;
	}

	:deep(.form-steps .n-step-content-header__title) {
		font-weight: 600;
	}

	@media (max-width: 640px) {
		.mode-selector__grid {
			grid-template-columns: 1fr;
		}
	}
</style>
